import {Typography} from "@mui/material";
import React, {useEffect, useState} from "react";
import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate} from "react-router-dom";
import {ReactComponent as BookmarkIcon} from "../../../../../assets/icons/bookmark.svg";
import {ReactComponent as DataSourceIcon} from "../../../../../assets/icons/catalog.svg";
import {ReactComponent as SettingsIcon} from "../../../../../assets/icons/clock-refresh.svg";
import {ReactComponent as EditIcon} from "../../../../../assets/icons/edit.svg";
import {ReactComponent as InfoOutline} from "../../../../../assets/icons/info_outline.svg";
import {ReactComponent as ListIcon} from "../../../../../assets/icons/list.svg";
import {ReactComponent as MessageSolvedSvg} from "../../../../../assets/icons/message_solved.svg";
import {ReactComponent as SettingsGearIcon} from "../../../../../assets/icons/settings.svg";
import {ReactComponent as UserIcon} from "../../../../../assets/icons/user.svg";
import {ReactComponent as UsersIcon} from "../../../../../assets/icons/users.svg";
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import InsertChartOutlinedOutlinedIcon from '@mui/icons-material/InsertChartOutlinedOutlined';
import {AdminButton, AdminMenuWrapper, Arrow, DropdownItem, DropdownMenu} from "./AdminDropdown.styled";
import {useFeatureConfig} from "../../../../../contexts/FeatureConfigContext";
import {FEATURE_KEYS} from "../../../../../models/admin-config.models";

const AdminDropdown = ({isOpen}: {isOpen: boolean}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const {t} = useTranslation();
    const {isFeatureEnabled} = useFeatureConfig();

    const [isExpanded, setIsExpanded] = useState(false);

    const menuItems = [
        {
            id: "users",
            path: "/admin/user/list",
            label: t('sidebar.users'),
            icon: <UserIcon />,
            enabled: true,
        },
        {
            id: "groups",
            path: "/admin/group/list",
            label: t('sidebar.groups'),
            icon: <UsersIcon />,
            enabled: true,
        },
        {
            id: "data-sources",
            path: "/admin/data-sources/list",
            label: t('sidebar.dataSourcesAdmin'),
            icon: <DataSourceIcon />,
            enabled: true,
        },
        {
            id: "scheduler",
            path: "/admin/scheduler",
            label: t('sidebar.scheduler'),
            icon: <SettingsIcon />,
            enabled: true,
        },
        {
            id: "statistics",
            path: "/admin/statistics",
            label: t('sidebar.statistics'),
            icon: <InsertChartOutlinedOutlinedIcon />,
            enabled: true,
        },
        {
            id: "reported-issues",
            path: "/admin/reported-issues",
            label: t('sidebar.reportedIssues'),
            icon: <MessageSolvedSvg />,
            enabled: true,
        },
        {
            id: "prompts",
            path: "/admin/prompts",
            label: t('sidebar.prompts'),
            icon: <EditIcon />,
            enabled: isFeatureEnabled(FEATURE_KEYS.DOCUMENT_ANALYSIS_KEY),
        },
        {
            id: "logs",
            path: "/admin/logs",
            label: t('sidebar.logs'),
            icon: <ListIcon />,
            enabled: true,
        },
        {
            id: "license-management",
            path: "/admin/license-management",
            label: t('sidebar.licenseManagement'),
            icon: <VerifiedUserOutlinedIcon />,
            enabled: true,
        },
        {
            id: "configuration",
            path: "/admin/configuration",
            label: t('sidebar.configuration'),
            icon: <SettingsGearIcon />,
            enabled: true,
        },
        {
            id: "info",
            path: "/admin/app-info",
            label: t('sidebar.appInfo'),
            icon: <InfoOutline />,
            enabled: true,
        },
    ];

    const isActiveItem = (itemPath: string) => location.pathname === itemPath || location.pathname.startsWith(`${itemPath}/`);
    const isAnyActive = menuItems.some((item) => isActiveItem(item.path));

    useEffect(() => {
        if (isAnyActive) {
            setIsExpanded(true);
        }
    }, [isAnyActive, location.pathname]);

    return (
        <AdminMenuWrapper>
            <AdminButton
                variant='text'
                onClick={() => setIsExpanded((prev) => !prev)}
                $active={isAnyActive}
                $isOpen={isOpen}
            >
                <BookmarkIcon/><span>{t('sidebar.adminPanel')}</span><Arrow $active={isAnyActive} $isExpanded={isExpanded} $isOpen={isOpen}/>
            </AdminButton>

            {isExpanded && (
                <DropdownMenu $open={isExpanded} $isOpen={isOpen}>
                    {menuItems
                        .filter(({enabled}) => enabled)
                        .map(({path, label, icon, id}) => (
                            <DropdownItem
                                key={id}
                                onClick={() => navigate(path)}
                                $active={isActiveItem(path)}
                                $isOpen={isOpen}
                            >
                                {icon}<Typography variant="body2" component="span">{label}</Typography>
                            </DropdownItem>
                        ))
                    }
                </DropdownMenu>
            )}
        </AdminMenuWrapper>
    );
};

export default AdminDropdown;
